import { Box, Button, Flex, Image, Input, Stack, Text } from '@chakra-ui/react'
import { transparentize } from 'color2k'
import React, { useState } from 'react'
import microsoftLogo from '../assets/static/images/microsoft.png'
import { useAuth } from '../context/AuthContext'
import { CLIENT_CONFIG } from '../core/client/buildConfig'
import type { ClientConfig } from '../core/client/ClientProvider'
import { useLanguage } from '../hooks/utility/useLanguage'

const LoginPage: React.FC = () => {
  const { signIn, signInWithPassword } = useAuth()
  const { t } = useLanguage()
  // Static per-client switch (config.json), deliberately not a runtime flag.
  const customLoginEnabled = (CLIENT_CONFIG as ClientConfig).auth?.custom_login === true
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const themeColors = (CLIENT_CONFIG as ClientConfig).theme.colors
  const loginCardOverlay = themeColors.loginCardOverlay ?? transparentize(themeColors.primary, 0.25)
  const loginCardOffsetY = (CLIENT_CONFIG as ClientConfig).theme.layout.loginCardOffsetY
  const loginCardTransform = loginCardOffsetY
    ? { base: `translateY(${loginCardOffsetY.base})`, md: `translateY(${loginCardOffsetY.md})` }
    : undefined

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    try {
      await signInWithPassword(username, password)
    } catch (err: any) {
      setError(err.message || 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Flex
      flexDirection='column'
      width='100wh'
      height='100vh'
      bgImage={
        CLIENT_CONFIG.theme.branding.backgroundImage
          ? `url(${CLIENT_CONFIG.theme.branding.backgroundImage})`
          : undefined
      }
      bg={!CLIENT_CONFIG.theme.branding.backgroundImage ? CLIENT_CONFIG.theme.colors.primary : undefined}
      bgSize='cover'
      bgPosition='center'
      bgRepeat='no-repeat'
      overflow='hidden'
      justifyContent='center'
      alignItems='center'
    >
      <Stack
        flexDir='column'
        mb='2'
        justifyContent='center'
        alignItems='center'
        transform={loginCardTransform}
      >
        <Box width={{ base: '80%', md: '400px' }} p={6}>
          <Stack spacing={4} p='1rem' bg={loginCardOverlay} borderRadius='md' backdropFilter='blur(8px)'>
            <Flex align='center' gap={10}>
              <Box textAlign='center'>
                <Text color='white'>{t('auth.welcomeCompany', { companyName: CLIENT_CONFIG.theme.branding.companyName })}</Text>
                <Text color='white'>{t('auth.loginInstructions')}</Text>
              </Box>
            </Flex>

            <Button
              leftIcon={<Image src={microsoftLogo} alt='Microsoft logo' boxSize='24px' />}
              borderRadius={0}
              fontSize='20px'
              width='full'
              onClick={signIn}
              // The card behind this button is a translucent wash of the primary
              // color, so the button inverts the usual solid styling: pale
              // surface, dark text.
              color='brand.800'
              bg='brand.100'
              _hover={{
                bg: 'brand.50',
              }}
            >
              {t('auth.loginWithMicrosoft')}
            </Button>

            {customLoginEnabled && (
              <>
                <Text
                  fontSize='sm'
                  textAlign='center'
                  cursor='pointer'
                  textDecoration='underline'
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                  color='white'
                  _hover={{ color: 'gray.300' }}
                >
                  {showPasswordForm ? t('auth.hidePasswordForm') : t('auth.loginWithPassword')}
                </Text>

                {showPasswordForm && (
                  <Box as='form' onSubmit={handlePasswordLogin} mt={4}>
                    <Stack spacing={3}>
                      <Input
                        placeholder='Username'
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        required
                        bg='white'
                      />
                      <Input
                        type='password'
                        placeholder='Password'
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        required
                        bg='white'
                      />
                      {error && (
                        <Text color='red.500' fontSize='sm'>
                          {error}
                        </Text>
                      )}
                      <Button
                        type='submit'
                        width='full'
                        isLoading={isLoading}
                        variant='solid'
                        colorScheme='primaryScheme'
                      >
                        {t('auth.login')}
                      </Button>
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Box>
      </Stack>
    </Flex>
  )
}

export default LoginPage
